/**
 * The filename a server put in `Content-Disposition`, or null.
 *
 * WHY THIS EXISTS AT ALL
 *
 * A download performed through `apiClient` cannot be a plain `<a href>` — the
 * route needs an `Authorization` header — so it arrives as a blob and is saved
 * with `a.download`. A blob URL carries no filename, so WITHOUT a `download`
 * attribute the browser saves the object id. Every other download on this
 * frontend therefore composes a name locally (`${ticketNumber}.pdf`), which is
 * right for a document the server generates on the fly and wrong for one a
 * customer uploaded: their file already has a name, the server sanitised it and
 * sent it back, and inventing a second one loses it.
 *
 * So the name is READ from the response rather than rebuilt from the ticket.
 *
 * `filename*` WINS, AND THAT IS THE WHOLE REASON TO PARSE RATHER THAN REGEX ONE
 *
 * RFC 6266 lets a server send both forms, and `fieldiq-api` does:
 *
 *   attachment; filename="Stamped FT.pdf"; filename*=UTF-8''Stamp%C3%A9%20FT.pdf
 *
 * The plain `filename` is an ASCII-folded fallback for ancient clients — on a
 * name with an accent it is the DEGRADED copy. A reader that takes the first
 * match it finds gets the worse answer whenever the two differ, and they only
 * differ on exactly the names where it matters. `filename*` is preferred here
 * regardless of parameter order, and only then does `filename` apply.
 *
 * Returns null rather than a guess when the header is absent or unparseable —
 * the caller then has a real fallback (the filename recorded on the ticket) and
 * must not be handed an empty string that reads as a name.
 */

/** Strip any directory component a header might carry. Display-only value. */
function basename(name: string): string {
  const cut = name.replace(/\\/g, '/');
  const last = cut.slice(cut.lastIndexOf('/') + 1);
  return last.trim();
}

export function filenameFromContentDisposition(header: unknown): string | null {
  if (typeof header !== 'string' || header === '') return null;

  // RFC 5987: `filename*=UTF-8''<pct-encoded>`. The charset and an optional
  // language tag precede the value; only UTF-8 and ISO-8859-1 are legal and
  // both decode acceptably through decodeURIComponent for our purposes.
  const extended = header.match(/filename\*\s*=\s*[^']*'[^']*'([^;]+)/i);
  if (extended) {
    try {
      const decoded = basename(decodeURIComponent(extended[1].trim()));
      if (decoded !== '') return decoded;
    } catch {
      // A malformed percent-escape throws. Fall through to the plain form,
      // which is the fallback the server sent it alongside.
    }
  }

  // The plain form, quoted or bare. `[^"]*` deliberately does not honour
  // backslash escapes: the API strips `"` from the name before it ever reaches
  // the header (sanitizeStampedFilename), so an escaped quote cannot occur, and
  // pretending to handle one would be untested code.
  const plain = header.match(/filename\s*=\s*(?:"([^"]*)"|([^;]+))/i);
  if (plain) {
    const raw = basename((plain[1] ?? plain[2] ?? '').trim());
    if (raw !== '') return raw;
  }

  return null;
}
